import { supabase } from "../../lib/supabaseClient";
import { mapProfile } from "./mappers";
import { getStoragePublicUrl } from "../../lib/supabaseClient";

function requireClient() {
  if (!supabase) throw new Error("Supabase is not configured.");
  return supabase;
}

async function enrichNotification(row) {
  const { data: profile } = await requireClient()
    .from("profiles")
    .select("*")
    .eq("id", row.actor_id)
    .maybeSingle();
  const { data: images } = await requireClient()
    .from("profile_images")
    .select("*")
    .eq("user_id", row.actor_id)
    .order("sort_order")
    .limit(4);

  const user = mapProfile(profile);
  const mappedImages = (images || []).map((img) => ({
    id: img.id,
    fileName: img.file_name,
    storagePath: img.storage_path,
    url: getStoragePublicUrl(img.storage_path),
  }));
  return {
    likeNotificationId: row.id,
    user: { ...user, images: mappedImages },
    images: mappedImages,
    conversationId: row.conversation_id,
    sendBirdUri: row.conversation_id,
    createdAt: row.created_at,
  };
}

export async function GetLikesNotifications(userId) {
  const client = requireClient();
  const { data, error } = await client
    .from("notifications")
    .select("*")
    .eq("recipient_id", userId)
    .eq("type", "like")
    .is("read_at", null)
    .order("created_at", { ascending: false });
  if (error) throw error;

  const likes = data || [];
  if (!likes.length) return [];

  // Exclude people the user has already matched with (like rows survive after mutual match).
  const { data: matches, error: matchError } = await client
    .from("matches")
    .select("user_a_id, user_b_id")
    .or(`user_a_id.eq.${userId},user_b_id.eq.${userId}`);
  if (matchError) throw matchError;

  const matchedIds = new Set(
    (matches || []).map((m) => (m.user_a_id === userId ? m.user_b_id : m.user_a_id))
  );

  const unmatchedLikes = likes.filter((row) => !matchedIds.has(row.actor_id));
  const staleLikeIds = likes.filter((row) => matchedIds.has(row.actor_id)).map((row) => row.id);

  // Clear matched like notifications so they don't keep appearing.
  if (staleLikeIds.length > 0) {
    await client
      .from("notifications")
      .update({ read_at: new Date().toISOString() })
      .in("id", staleLikeIds);
  }

  return Promise.all(unmatchedLikes.map(enrichNotification));
}

export async function GetMatchesNotifications(userId) {
  const { data, error } = await requireClient()
    .from("notifications")
    .select("*")
    .eq("recipient_id", userId)
    .eq("type", "match")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return Promise.all((data || []).map(enrichNotification));
}

export async function DeleteNotification(notificationId) {
  const { error } = await requireClient().from("notifications").delete().eq("id", notificationId);
  if (error) throw error;
  return { success: true };
}
